import { useEffect, useState } from 'react'
import { useRotatingHeroMessage } from '../../constants/heroMessages'

interface Props {
  firstName: string
}

function greetingForHour(hour: number): string {
  if (hour < 12) return 'Good morning'
  if (hour < 17) return 'Good afternoon'
  return 'Good evening'
}

function msUntilNextBoundary(now: Date): number {
  const hour = now.getHours()
  const boundaryHour = hour < 12 ? 12 : hour < 17 ? 17 : 24
  const next = new Date(now)
  next.setHours(boundaryHour, 0, 0, 0)
  return Math.max(next.getTime() - now.getTime(), 1000)
}

function useLocalGreeting(): string {
  const [greeting, setGreeting] = useState(() => greetingForHour(new Date().getHours()))

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout>

    const schedule = () => {
      timer = setTimeout(() => {
        setGreeting(greetingForHour(new Date().getHours()))
        schedule()
      }, msUntilNextBoundary(new Date()))
    }

    schedule()
    return () => clearTimeout(timer)
  }, [])

  return greeting
}

export default function HeroBanner({ firstName }: Props) {
  const { message, visible, fadeMs } = useRotatingHeroMessage()
  const greeting = useLocalGreeting()

  return (
    <div
      className="relative w-full overflow-hidden rounded-2xl"
      style={{ minHeight: 220, maxHeight: 280, background: '#1a0f0e', isolation: 'isolate' }}
    >
      <div
        className="absolute inset-0 pointer-events-none"
        aria-hidden="true"
        style={{
          backgroundImage: 'url(/images/topo-pattern-slide.png)',
          backgroundSize: '135% auto',
          backgroundPosition: 'center center',
          backgroundRepeat: 'no-repeat',
          opacity: 0.95,
          mixBlendMode: 'screen',
          filter: 'contrast(1.8) brightness(1.5)',
        }}
      />

      <div className="relative z-10 flex flex-col items-center justify-center text-center px-6 py-12" style={{ minHeight: 220 }}>
        <img
          src="/images/pinnacle-logo-darkbg.png"
          alt="Pinnacle"
          className="h-10 mb-4"
        />
        <h1 className="text-white font-sans font-semibold mb-3" style={{ fontSize: 40 }}>
          {greeting}, {firstName}
        </h1>
        <p
          className="text-white font-sans italic max-w-2xl"
          style={{
            fontSize: 22,
            opacity: visible ? 0.85 : 0,
            transition: `opacity ${fadeMs}ms ease-in-out`,
          }}
        >
          &ldquo;{message}&rdquo;
        </p>
      </div>
    </div>
  )
}
